import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ArrowDownCircle, ArrowUpCircle, Info, Package, RefreshCw, Scale, ShoppingCart, Store, Users } from 'lucide-react';
import { apiService, type AdminShopItemDTO, type AdminShopPlayerDTO, type AdminShopResponse } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { formatPlayerName } from '@/utils/playerName';
import { BarList, ColumnChart, type ColumnDatum } from './JobsCharts';
import { dayLabel, formatInstant, shortDay, targetLabel } from './jobStats';
import { formatMoney, medal, shortUuid } from './moneyLeaderboard';
import { formatMoneyShort, sectionError } from './shopFactions';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';

const PERIODS = [7, 14, 30, 90] as const;

/** Lignes des tableaux d'objets (le satellite en rend 30, triées par argent). */
const TABLE_ROWS = 15;

/**
 * Section « Admin shop » de l'espace Statistiques (Paladium) : argent dépensé et gagné au shop serveur, objets les
 * plus achetés et vendus, joueurs qui achètent et vendent le plus. Lu par le satellite (1.0.26) dans les
 * transactions de l'argent : AdminShopMod n'a pas de base à lui.
 */
export function AdminShopSection() {
  const gameId = useGameStore((s) => s.activeGameId);
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<AdminShopResponse | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getAdminShopStats(days);
    if (res.data) { setData(res.data); setError(null); }
    else { setData(null); setError({ status: res.status, message: sectionError(res.status, res.error, "les statistiques de l'admin shop") }); }
    setLoading(false);
  }, [days, gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  if (loading && !data && !error) return <div className={lb.state}><div className={lb.spinner} /><p>Lecture de l'admin shop...</p></div>;
  if (error && !data) {
    return (
      <div className={lb.state}>
        {error.status === 404 ? <Info size={32} className={styles.stateIcon} /> : <AlertCircle size={32} />}
        <h3>{error.status === 404 ? "Statistiques de l'admin shop pas encore servies" : "Admin shop illisible"}</h3>
        <p className={styles.stateText}>{error.message}</p>
        <button className={lb.buttonGhost} onClick={() => void load()}><RefreshCw size={16} /> Vérifier de nouveau</button>
      </div>
    );
  }
  if (!data) return null;
  if (!data.available) return <div className={lb.state}><Store size={32} /><h3>Pas d'admin shop ici</h3><p>{data.reason}</p></div>;

  const t = data.totals!;
  const balance = t.spent - t.earned;
  const cards = [
    { icon: ArrowDownCircle, value: formatMoneyShort(t.spent), label: 'Dépensé par les joueurs', sub: `${t.buys.toLocaleString('fr-FR')} achats` },
    { icon: ArrowUpCircle, value: formatMoneyShort(t.earned), label: 'Gagné par les joueurs', sub: `${t.sells.toLocaleString('fr-FR')} ventes` },
    {
      icon: Scale, value: formatMoneyShort(Math.abs(balance)),
      label: balance >= 0 ? "Argent retiré de l'économie" : "Argent ajouté à l'économie",
      sub: 'Dépenses moins gains au shop',
    },
    { icon: Users, value: `${t.buyers.toLocaleString('fr-FR')} / ${t.sellers.toLocaleString('fr-FR')}`, label: 'Acheteurs / vendeurs', sub: 'Joueurs distincts' },
  ];
  const spentCols: ColumnDatum[] = data.daily.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: [{ id: 's', label: 'Dépensé', value: d.spent, color: 'var(--job-2)' }],
    note: `${d.buys.toLocaleString('fr-FR')} achats`,
  }));
  const earnedCols: ColumnDatum[] = data.daily.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: [{ id: 'e', label: 'Gagné', value: d.earned, color: 'var(--job-3)' }],
    note: `${d.sells.toLocaleString('fr-FR')} ventes`,
  }));
  const soldMoney = data.sold.reduce((s, r) => s + r.money, 0);

  return (
    <div className={styles.section}>
      <div className={lb.cards}>
        {cards.map((c) => (
          <div key={c.label} className={lb.card}>
            <div className={lb.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
            <div className={lb.cardText}>
              <span className={lb.cardValue}>{c.value}</span>
              <span className={lb.cardLabel}>{c.label}</span>
              <span className={lb.cardSub}>{c.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.info}><Info size={16} />
        <span>
          Achats et ventes au shop serveur sur {data.days} jours, lus dans les mouvements d'argent
          {data.since ? ` (premier mouvement connu le ${formatInstant(data.since).split(' à ')[0]})` : ''}.
          {data.buysWithoutItem > 0 && ` ${data.buysWithoutItem.toLocaleString('fr-FR')} achats (${formatMoney(Math.round(data.buysWithoutItemMoney))}) n'ont pas d'objet connu : le jeu ne note l'objet acheté qu'à partir d'AdminShopMod 0.0.23.`}
        </span>
      </div>

      <section className={styles.block}>
        <div className={styles.blockHead}>
          <h3><ShoppingCart size={18} /> Argent par jour</h3>
          <select className={lb.select} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Période">
            {PERIODS.map((p) => <option key={p} value={p}>{p} jours</option>)}
          </select>
        </div>
        <div className={styles.grid2}>
          <div>
            <h4 className={styles.blockSub}>Dépensé par les joueurs (achats)</h4>
            <ColumnChart data={spentCols} formatValue={formatMoneyShort} ariaLabel="Argent dépensé au shop par jour" height={190} />
          </div>
          <div>
            <h4 className={styles.blockSub}>Gagné par les joueurs (ventes)</h4>
            <ColumnChart data={earnedCols} formatValue={formatMoneyShort} ariaLabel="Argent gagné au shop par jour" height={190} />
          </div>
        </div>
      </section>

      <div className={styles.grid2}>
        <ItemsBlock title="Objets les plus vendus" rows={data.sold} totalMoney={soldMoney}
          empty="Aucune vente au shop sur la période." color="var(--job-3)" />
        <ItemsBlock title="Objets les plus achetés" rows={data.bought} totalMoney={data.bought.reduce((s, r) => s + r.money, 0)}
          empty={data.buysWithoutItem > 0
            ? "Les achats de la période n'ont pas d'objet connu : il sera noté avec AdminShopMod 0.0.23."
            : 'Aucun achat au shop sur la période.'}
          color="var(--job-2)" />
      </div>

      <div className={styles.grid2}>
        <PlayersBlock title="Joueurs qui dépensent le plus" rows={data.topBuyers} unit="dépensé" />
        <PlayersBlock title="Joueurs qui vendent le plus" rows={data.topSellers} unit="gagné" />
      </div>
      {data.namesPartial && (
        <div className={lb.notice}><AlertCircle size={16} /><span>Certains joueurs n'ont pas encore de pseudo connu : leur uuid est affiché à la place.</span></div>
      )}
    </div>
  );
}

function ItemsBlock({ title, rows, totalMoney, empty, color }: {
  title: string; rows: AdminShopItemDTO[]; totalMoney: number; empty: string; color: string;
}) {
  return (
    <section className={styles.block}>
      <div className={styles.blockHead}><h3><Package size={18} /> {title}</h3></div>
      {rows.length === 0 ? <p className={lb.empty}>{empty}</p> : (
        <>
          <BarList formatValue={formatMoneyShort} rows={rows.slice(0, 8).map((r) => ({
            id: r.item, label: targetLabel(r.item), color, value: r.money,
            share: totalMoney > 0 ? (r.money * 100) / totalMoney : 0,
            sub: `${r.quantity.toLocaleString('fr-FR')} unités`,
          }))} />
          <div className={lb.tableWrap}>
            <table className={lb.table}>
              <thead><tr><th>Objet</th><th className={lb.num}>Quantité</th><th className={lb.num}>Argent</th><th className={lb.num}>Prix moyen</th><th className={lb.num}>Fois</th></tr></thead>
              <tbody>
                {rows.slice(0, TABLE_ROWS).map((r) => (
                  <tr key={r.item}>
                    <td title={r.item}>{targetLabel(r.item)}</td>
                    <td className={lb.num}>{r.quantity.toLocaleString('fr-FR')}</td>
                    <td className={lb.num}>{formatMoney(Math.round(r.money))}</td>
                    <td className={lb.num}>{r.unitPrice.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}&nbsp;$</td>
                    <td className={lb.num}>{r.transactions.toLocaleString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function PlayersBlock({ title, rows, unit }: { title: string; rows: AdminShopPlayerDTO[]; unit: string }) {
  return (
    <section className={styles.block}>
      <div className={styles.blockHead}><h3><Users size={18} /> {title}</h3></div>
      {rows.length === 0 ? <p className={lb.empty}>Personne sur la période.</p> : (
        <div className={lb.tableWrap}>
          <table className={lb.table}>
            <thead><tr><th>Rang</th><th>Joueur</th><th className={lb.num}>{unit === 'dépensé' ? 'Dépensé' : 'Gagné'}</th><th className={lb.num}>Fois</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const m = medal(r.rank);
                return (
                  <tr key={r.uuid}>
                    <td><span className={`${lb.rank} ${m ? lb[m] : ''}`}>{r.rank}</span></td>
                    <td><span className={lb.playerName}>{formatPlayerName(r.name, r.displayName, shortUuid(r.uuid))}</span></td>
                    <td className={lb.num}>{formatMoney(Math.round(r.money))}</td>
                    <td className={lb.num}>{r.transactions.toLocaleString('fr-FR')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
